import React, {useCallback, useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import DynamicBreadcrumbs from "../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";

import {
  BreadcrumbsContainer,
  ContentPage,
  ConversationPageContent,
  ConversationPageHeader,
  ConversationPageWrapper
} from "./ConversationPage.styled";
import {useStore} from "../../../../hooks";
import {ConversationItemCard, HeaderView} from "../components";
import Spinner from "../../../../components/common/Spinner";
import {ConversationDrawer} from "../components/MarketingTab/MarketingTab.styled";
import {Drawer} from "@mui/material";
import {ConversationView} from "../components/ConversationView/ConversationView";
import {ConfirmationModal, GeneralMessageModal} from "../Section/Marketing/Components/Modal";

export const ConversationList = observer(() => {
  const { authStore, marketingStore, listUserStore, conversationsStore, conversationListStore } = useStore();

  const [isConversationOpen, setIsConversationOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    if (authStore.selectedAccount) {
      listUserStore.onFetchUsers(authStore.selectedAccount.id);
      conversationsStore.setAccount(authStore.selectedAccount.id);
      marketingStore.init({ customer: authStore.selectedAccount.id });
      conversationsStore.getTriggers();
    }
  }, [authStore.selectedAccount]);

  const listCampaigns = useMemo(() => [...marketingStore.campaigns].map((campaign, index) => {
    return {
      ...campaign,
      conversationType: "Marketing",
      conversationNumber: [...marketingStore.campaigns].length - index
    };
  }), [marketingStore.campaigns]);

  const listTriggers = useMemo(() => [...conversationsStore.triggersList].map((trigger, index) => {
    return {
      ...trigger,
      conversationType: "Trigger",
      conversationNumber: [...conversationsStore.triggersList].length - index
    };
  }), [marketingStore.campaigns]);

  const listConversations = useMemo(() => {
    let list = listTriggers.concat(listCampaigns);

    if (!!marketingStore.filters.search) {
      list = list.filter((item) => item.name && item.name.toLowerCase().includes(marketingStore.filters.search.toLowerCase()));
    }

    // filter by owner
    if (marketingStore.filters.ownerIds.length > 0) {
      list = list.filter((item) => marketingStore.filters.ownerIds.includes(item.owner));
    }

    // filter by type
    if (marketingStore.filters.type.length > 0) {
      list = list.filter((item) => marketingStore.filters.type.includes(item.conversationType.toLowerCase()));
    }

    // filter by status
    if (marketingStore.filters.status.length > 0) {
      list = list.filter((item) => marketingStore.filters.status.includes(item.status.toLowerCase()));
    }

    return list;
  }, [listCampaigns, listTriggers, marketingStore.filters.search, marketingStore.filters.ownerIds, marketingStore.filters.type, marketingStore.filters.status]);

  const sortedMessages = useMemo(() =>
    listConversations.sort((a, b) =>
      new Date(b.last_modified) - new Date(a.last_modified)
    ), [listConversations]);
  
  const breadcrumbItems = useMemo(
    () => [{ label: "Conversations Starters", path: "/v2/conversations" }],
    []
  );

  const handleFetchConversations = async (campaign) => {
    try {
      const conversations = await conversationListStore.fetchConversations(campaign.id);
      // ... rest of the code ...
    } catch (error) {
      // ... error handling ...
    }
  };

  const handleOpenConversation = useCallback(async (campaign) => {
    setIsConversationOpen(true);
    setSelectedCampaign(campaign);
    setSelectedConversation(null);
    
    try {
      const conversations = await conversationListStore.fetchConversations(campaign.id);
      const approvedMessageIds = new Set();

      const formattedConversations = Object.entries(conversations).map(([email, convData]) => {
        const allMessages = convData.map(conv => {
          let messageContent = conv.message;
          try {
            if (typeof conv.message === 'string' && (conv.message.startsWith('{') || conv.message.startsWith('['))) {
              const parsedMessage = JSON.parse(conv.message);
              if (parsedMessage.message) {
                messageContent = parsedMessage.message;
              }
            }
          } catch (e) {
            // If parsing fails, use the original message
          }
          
          let status = conv.status || 'PENDING_SEND';
          
          if (status === 'APPROVED') status = 'SENT';
          
          if (status === 'SENT') {
            approvedMessageIds.add(conv.id);
          }

          return {
            id: conv.id,
            sender: conv.sender,
            message: messageContent,
            insert_date: conv.insert_date,
            channel: conv.channel,
            isAgent: conv.sender.includes('dev') || conv.role === 'AGENT',
            status: status,
            role: conv.role,
            approvedAt: conv.approvedAt || (status === 'SENT' ? new Date().toISOString() : null)
          };
        });

        const sortedMessages = allMessages.sort((a, b) =>
          new Date(a.insert_date) - new Date(b.insert_date)
        );

        const latestMessage = sortedMessages[sortedMessages.length - 1];

        return {
          id: `conv-${email}-${Date.now()}`,
          contact_name: email.split('@')[0],
          contact_email: email,
          last_message: latestMessage.message,
          last_updated: latestMessage.insert_date,
          channel: latestMessage.channel,
          status: latestMessage.status,
          messages: sortedMessages,
          conversation_id: convData[0].conversation_id || null
        };
      });

      setSelectedCampaign(prev => ({
        ...prev,
        conversations: formattedConversations,
        approvedMessageIds: approvedMessageIds
      }));

      if (formattedConversations.length > 0) {
        setSelectedConversation(formattedConversations[0]);
      }
    } catch (error) {
      // Silent error handling
    }
  }, [conversationListStore]);
  
  const handleCloseConversation = useCallback(() => {
    setIsConversationOpen(false);
    setSelectedCampaign(null);
    setSelectedConversation(null);
  }, []);
  
  return (
    <ContentPage>
      <GeneralMessageModal />
      <ConfirmationModal />
      <BreadcrumbsContainer>
        <DynamicBreadcrumbs items={breadcrumbItems} currentPath="/v2/conversations" />
      </BreadcrumbsContainer>
      <ConversationPageWrapper>
        <ConversationPageHeader>
          <HeaderView users={listUserStore.users} />
        </ConversationPageHeader>
        <ConversationPageContent>
          {(marketingStore.isLoading || conversationsStore.isLoading) && <Spinner />}
          {(!marketingStore.isLoading && !conversationsStore.isLoading) && sortedMessages.map((conversation) => {
            return (
              <ConversationItemCard conversation={conversation} users={listUserStore.users} onOpenInbox={handleOpenConversation} />
            );
          })}
        </ConversationPageContent>
      </ConversationPageWrapper>
      <Drawer
        anchor="right"
        open={isConversationOpen}
        onClose={handleCloseConversation}
        PaperProps={{
          sx: {
            width: 'calc(100% - 72px)',
            height: '100%',
            maxWidth: 'none',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            left: '72px',
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
          },
          '& .MuiBackdrop-root': {
            left: '72px',
          },
        }}
      >
        <ConversationDrawer>
          {selectedCampaign && (
            <ConversationView
              campaign={selectedCampaign}
              onClose={handleCloseConversation}
              selectedConversation={selectedConversation}
              onSelectConversation={setSelectedConversation}
            />
          )}
        </ConversationDrawer>
      </Drawer>
    </ContentPage>
  );
});

export default ConversationList;
